import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConversationProvider } from "@elevenlabs/react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { I18nProvider } from "./contexts/I18nContext";

// Eager-loaded: needed on first paint for login/auth flows
import EliosLandingPage from "./pages/EliosLandingPage";
import EliosLoginPage from "./pages/EliosLoginPage";
import EliosAuthCallback from "./pages/EliosAuthCallback";
import EliosLayout from "./components/layout/EliosLayout";

// Lazy-loaded Elios pages
const EliosResetPasswordPage = lazy(() => import("./pages/EliosResetPasswordPage"));
const EliosDashboardPage = lazy(() => import("./pages/EliosDashboardPage"));
const EliosInboundPage = lazy(() => import("./pages/EliosInboundPage"));
const EliosSettingsPage = lazy(() => import("./pages/EliosSettingsPage"));
const EliosRubricaPage = lazy(() => import("./pages/EliosRubricaPage"));
const EliosOutboundPage = lazy(() => import("./pages/EliosOutboundPage"));
const EliosCampagnePage = lazy(() => import("./pages/EliosCampagnePage"));
const EliosLeadsPage = lazy(() => import("./pages/EliosLeadsPage"));
const EliosCalendarPage = lazy(() => import("./pages/EliosCalendarPage"));
const EliosAnalyticsPage = lazy(() => import("./pages/EliosAnalyticsPage"));
const EliosPromemoriaPage = lazy(() => import("./pages/EliosPromemoriaPage"));
const EliosAssegnatePage = lazy(() => import("./pages/EliosAssegnatePage"));
const EliosProfilaPage = lazy(() => import("./pages/EliosProfilaPage"));
const EliosFollowupPage = lazy(() => import("./pages/EliosFollowupPage"));
const EliosAnalysisPage = lazy(() => import("./pages/EliosAnalysisPage"));
const EliosDocsPage = lazy(() => import("./pages/EliosDocsPage"));
const EliosTicketingPage = lazy(() => import("./pages/EliosTicketingPage"));
const EliosWhatsAppPage = lazy(() => import("./pages/EliosWhatsAppPage"));
const EliosHealthPage = lazy(() => import("./pages/EliosHealthPage"));
const AdminEliosSystemPage = lazy(() => import("./pages/AdminEliosSystemPage"));
const DistributorDashboardPage = lazy(() => import("./pages/DistributorDashboardPage"));
const FornitorePortalPage = lazy(() => import("./pages/FornitorePortalPage"));

// Route guard for admin-only pages. Logged-out users go to login; non-admin
// roles (e.g. elios_client) are bounced to their dashboard.
const ADMIN_ROLES = ["superadmin", "admin", "vice_superadmin"];
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }
  if (!user) return <Navigate to="/elios/login" replace />;
  if (!ADMIN_ROLES.includes(user.role)) return <Navigate to="/elios/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <ConfirmProvider>
            <ConversationProvider>
              <Toaster richColors position="top-right" />
              <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}>
                <Routes>
            {/* Public landing page */}
            <Route path="/" element={<EliosLandingPage />} />

            {/* Auth routes (no layout) */}
            <Route path="/elios/login" element={<EliosLoginPage />} />
            <Route path="/elios/reset-password" element={<EliosResetPasswordPage />} />
            <Route path="/elios/auth/callback" element={<EliosAuthCallback />} />

            {/* Admin panel */}
            <Route path="/admin/elios-system" element={<AdminEliosSystemPage />} />

            {/* Distributor portal */}
            <Route path="/distributor/dashboard" element={<DistributorDashboardPage />} />

            {/* Provider (fornitore) portal — public, token-gated via ?token= */}
            <Route path="/fornitore/:id" element={<FornitorePortalPage />} />

            {/* B2B Client Portal (EliosLayout with sidebar) */}
            <Route element={<EliosLayout />}>
              <Route path="/elios/dashboard" element={<EliosDashboardPage />} />
              <Route path="/elios/inbound" element={<EliosInboundPage />} />
              <Route path="/elios/rubrica" element={<EliosRubricaPage />} />
              <Route path="/elios/outbound" element={<EliosOutboundPage />} />
              <Route path="/elios/assegnate" element={<EliosAssegnatePage />} />
              <Route path="/elios/profili" element={<EliosProfilaPage apiPrefix="/elios-client" />} />
              <Route path="/elios/followup" element={<EliosFollowupPage />} />
              <Route path="/elios/analisi-ai" element={<EliosAnalysisPage />} />
              <Route path="/elios/campagne" element={<EliosCampagnePage />} />
              <Route path="/elios/leads" element={<EliosLeadsPage />} />
              <Route path="/elios/calendario" element={<EliosCalendarPage />} />
              <Route path="/elios/analytics" element={<EliosAnalyticsPage />} />
              <Route path="/elios/promemoria" element={<EliosPromemoriaPage />} />
              <Route path="/elios/settings" element={<EliosSettingsPage />} />
              <Route path="/elios/ticketing" element={<EliosTicketingPage />} />
              <Route path="/elios/whatsapp" element={<EliosWhatsAppPage />} />
              <Route path="/elios/docs" element={<EliosDocsPage />} />
              <Route path="/elios/health" element={<RequireAdmin><EliosHealthPage /></RequireAdmin>} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/elios/login" replace />} />
                </Routes>
              </Suspense>
            </ConversationProvider>
          </ConfirmProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
